import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';

import { useUser } from '../../context/UserContext';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import { useOrganization, useOrganizationLocations } from '../../hooks/useOrganizationData';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Settings.css';

const LocationManagement = ({ userOrganizationId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin, isSuperAdmin, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const activeOrganizationId = useSelectedOrganization(userOrganizationId);
  const notification = useNotification();

  const { data: locations = [], isLoading: isLoadingLocations, refetch: refetchLocations } =
    useOrganizationLocations(activeOrganizationId);
  const { data: organization, isLoading: isLoadingOrg } = useOrganization(activeOrganizationId);
  const isLoading = isLoadingLocations || isLoadingOrg;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);

  // Calculate location usage
  const maxLocations = organization?.max_locations || null;
  const currentLocationCount = locations.length;
  const isAtLimit = maxLocations !== null && currentLocationCount >= maxLocations;
  const isSuperAdminUser = isSuperAdmin();

  useEffect(() => {
    if (!isUserLoading && !isAdmin()) {
      notification.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [isAdmin, isUserLoading, navigate, notification]);

  const handleCreateClick = () => {
    navigate('/settings/locations/new');
  };

  const handleEditClick = (location) => {
    navigate(`/settings/locations/${location._id}`, { state: { location } });
  };

  const handleDeleteClick = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete) return;

    setDeleteDialogOpen(false);
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/retailerlocations/${locationToDelete._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('Location deleted successfully');
      refetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      notification.error('Failed to delete location');
    }
    setLocationToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLocationToDelete(null);
  };

  if (isUserLoading || isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <h1 className="settings-page-title">Locations</h1>
            <p className="settings-page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-card">
            <div className="settings-loading">Loading locations...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Location"
        description="Are you sure you want to delete this location? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <h1 className="settings-page-title">Locations</h1>
          <p className="settings-page-subtitle">
            Manage store locations for your organization
            {maxLocations !== null && !isSuperAdminUser && (
              <span style={{ marginLeft: '8px' }}>
                <span className={`settings-count-badge ${isAtLimit ? 'at-limit' : ''}`}>
                  {currentLocationCount} / {maxLocations} locations
                </span>
              </span>
            )}
          </p>
        </div>
        <div className="settings-page-header-right">
          {isAtLimit && !isSuperAdminUser ? (
            <div className="location-limit-warning">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>Location limit reached. Contact support to upgrade.</span>
            </div>
          ) : (
            <button className="settings-btn primary" onClick={handleCreateClick}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Create New Location
            </button>
          )}
        </div>
      </div>

      <div className="settings-page-content">
        <div className="settings-card">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Store Name</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Manager</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location._id}>
                  <td>
                    <span className="settings-table-primary">{location.store_name}</span>
                  </td>
                  <td>{location.address_line1}</td>
                  <td>{location.city}</td>
                  <td>{location.state}</td>
                  <td>
                    {location.manager_first_name || location.manager_last_name
                      ? `${location.manager_first_name || ''} ${location.manager_last_name || ''}`.trim()
                      : '-'}
                  </td>
                  <td>
                    <div className="settings-table-actions">
                      <button
                        className="settings-action-btn primary"
                        onClick={() => handleEditClick(location)}
                        title="Edit location"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="settings-action-btn danger"
                        onClick={() => handleDeleteClick(location)}
                        title="Delete location"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {locations.length === 0 && (
                <tr>
                  <td colSpan="6" className="settings-table-empty">
                    No locations found. Click "Create New Location" to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;
